import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    setOperationStep,
    setOperationData,
    resetOperation,
    setCommandMessage,
} from '../../../redux/operationSlice';
import * as THREE from 'three';
import { generateUniqueId } from '../../../utils/generateUniqueId'
import { COMMANDS } from '../../../utils/commonProperties';
import { createDrawingClickHandler, getOrthoSnappedPoint } from "./useDrawingEvents";
import { getSnappedPoint } from '../../../shared/snap/getSnappedPoint';
import { createSnapMarker } from '../../../shared/snap/createSnapMarker';
import { createEscapeHandler } from '../../../utils/escapeHandler';
import { DrawCommand } from '../../../modules/undoRedo';
import { addUserEntity } from '../../../redux/dxfSlice';

const useDrawLine = (scene, camera, renderer, snapPoints) => {
    const lastMouse = useRef({ x: 0, y: 0 });
    const tempLineRef = useRef(null);
    const snapMarkerRef = useRef(null);
    const snappedPointRef = useRef(null);
    const inputBuffer = useRef('');

    const { commandType, step, data } = useSelector((state) => state.operation);
    const { gridMode, objectSnap, snapMode, orthoMode } = useSelector((state) => state.mods);
    const { activeLayerName } = useSelector((state) => state.layer);
    const activeTabId = useSelector(state => state.projectTabs.activeTabId);

    const dispatch = useDispatch();

    useEffect(() => {
        if (commandType !== COMMANDS.DRAW_LINE || !renderer || !camera || !scene) return;

        const domElement = renderer.domElement;

        const handleLinePoint = (point) => {
            // Eğer ortho açıksa ve adım 1 ise orto noktasına hizala
            if (step === 1 && orthoMode) {
                const { startPoint } = data;
                point = getOrthoSnappedPoint(
                    new THREE.Vector3(startPoint.x, startPoint.y, startPoint.z),
                    point
                );
            }

            if (step === 0) {
                dispatch(setCommandMessage("2. noktayı seçin"));
                dispatch(setOperationData({ startPoint: { x: point.x, y: point.y, z: point.z } }));
                dispatch(setOperationStep(1));

                const geometry = new THREE.BufferGeometry().setFromPoints([point, point]);
                const material = new THREE.LineBasicMaterial({ color: 0xffff00 });
                const line = new THREE.Line(geometry, material);
                scene.add(line);
                tempLineRef.current = line;

            } else if (step === 1) {
                const { startPoint } = data;
                const endPoint = point;
                const entityId = generateUniqueId();
                const geometry = new THREE.BufferGeometry().setFromPoints([
                    new THREE.Vector3(startPoint.x, startPoint.y, startPoint.z),
                    point
                ]);
                const material = new THREE.LineBasicMaterial({ color: 0xffffff });
                const line = new THREE.Line(geometry, material);
                line.userData = {
                    id: entityId,
                    isSelectable: true,
                    type: "line",
                    start: { x: startPoint.x, y: startPoint.y, z: startPoint.z },
                    end: { x: endPoint.x, y: endPoint.y, z: endPoint.z },
                    layerName: activeLayerName,
                };
                scene.add(line);

                // Redux'a DXF entity olarak kaydet (JBZ'ye dahil edilecek)
                dispatch(addUserEntity({
                    type: 'LINE',
                    vertices: [
                        { x: startPoint.x, y: startPoint.y, z: startPoint.z || 0 },
                        { x: endPoint.x, y: endPoint.y, z: endPoint.z || 0 }
                    ],
                    layer: activeLayerName || '0',
                    _userEntityId: entityId,
                    _userDrawn: true,
                    _tabId: activeTabId || 'mimari',
                }));

                // Undo-redo kaydı
                const manager = window.undoRedoManager;
                if (manager) {
                    const tx = manager.beginTransaction('Draw Line');
                    manager.addCommand(new DrawCommand(line));
                    manager.commitTransaction(tx);
                }

                if (tempLineRef.current) {
                    scene.remove(tempLineRef.current);
                    tempLineRef.current.geometry.dispose();
                    tempLineRef.current.material.dispose();
                }

                const tempGeometry = new THREE.BufferGeometry().setFromPoints([point, point]);
                const tempMaterial = new THREE.LineBasicMaterial({ color: 0xffff00 });
                const tempLine = new THREE.Line(tempGeometry, tempMaterial);
                scene.add(tempLine);
                tempLineRef.current = tempLine;

                dispatch(setOperationData({ startPoint: { x: point.x, y: point.y, z: point.z } }));
                dispatch(setCommandMessage("sonraki noktayı seçin veya Enter ile bitirin."));
            }

        };

        const handleClick = createDrawingClickHandler({
            scene,
            camera,
            renderer,
            snapPoints,
            snapMarkerRef,
            gridMode,
            objectSnap,
            snapMode,
            onPointSelected: handleLinePoint
        });

        const handleMouseMove = (event) => {
            if (!renderer || !camera || !scene) return;

            const rect = domElement.getBoundingClientRect();
            const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
            lastMouse.current = { x, y };

            const mouseNDC = new THREE.Vector2(x, y);
            const point = new THREE.Vector3(x, y, 0).unproject(camera);

            const { finalPoint, snapped, snapSource } = getSnappedPoint(mouseNDC, point, snapPoints, camera, renderer, { snapMode: snapMode });

            // Snap marker — her seferinde yeniden oluştur (tip değişebilir)
            if (snapMarkerRef.current) {
                scene.remove(snapMarkerRef.current);
                if (snapMarkerRef.current.geometry) snapMarkerRef.current.geometry.dispose();
                if (snapMarkerRef.current.material) snapMarkerRef.current.material.dispose();
                snapMarkerRef.current = null;
            }
            if (snapped && snapSource) {
                const marker = createSnapMarker(snapSource);
                marker.position.copy(snapSource.position);
                scene.add(marker);
                snapMarkerRef.current = marker;
            }

            // Ortho aktifse, önizleme çizgisini hizala
            let previewPoint = finalPoint;
            if (step === 1 && orthoMode) {
                const { startPoint } = data;
                previewPoint = getOrthoSnappedPoint(
                    new THREE.Vector3(startPoint.x, startPoint.y, startPoint.z),
                    finalPoint
                );
            }

            // Önizleme çizgisi güncelle
            if (step === 1 && tempLineRef.current) {
                const { startPoint } = data;
                const sp = new THREE.Vector3(startPoint.x, startPoint.y, startPoint.z);
                tempLineRef.current.geometry.setFromPoints([sp, previewPoint]);
            }

        };

        const escapeHandler = createEscapeHandler({
            scene,
            dispatch,
            tempObjectRef: tempLineRef,
            snapMarkerRef,
            inputBuffer,
            additionalCleanup: () => {
                snappedPointRef.current = null;
                dispatch(setCommandMessage(""));
            }
        });

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                escapeHandler(e);
            } else if (e.key === 'Enter' && step === 1 && !inputBuffer.current) {
                // Enter (input yok) → çizimi bitir
                escapeHandler({ key: 'Escape' });
            } else if (e.key === 'Enter' && step === 1 && inputBuffer.current) { // Let user enter length by typing
                const distance = parseFloat(inputBuffer.current);
                if (!isNaN(distance)) {
                    const { startPoint } = data;
                    const sp = new THREE.Vector3(startPoint.x, startPoint.y, startPoint.z);
                    const ep = new THREE.Vector3();

                    const dir = tempLineRef.current.geometry.attributes.position.array;
                    const dx = dir[3] - dir[0];
                    const dy = dir[4] - dir[1];
                    const dz = dir[5] - dir[2];
                    const direction = new THREE.Vector3(dx, dy, dz).normalize();

                    ep.copy(sp).addScaledVector(direction, distance);

                    const entityId2 = generateUniqueId();
                    const geometry = new THREE.BufferGeometry().setFromPoints([sp, ep]);
                    const material = new THREE.LineBasicMaterial({ color: 0xffffff });
                    const line = new THREE.Line(geometry, material);
                    line.userData = {
                        id: entityId2,
                        isSelectable: true,
                        type: "line",
                        pos: [sp, ep],
                        layerName: activeLayerName,
                    };

                    scene.add(line);

                    // Redux'a DXF entity olarak kaydet
                    dispatch(addUserEntity({
                        type: 'LINE',
                        vertices: [
                            { x: sp.x, y: sp.y, z: sp.z || 0 },
                            { x: ep.x, y: ep.y, z: ep.z || 0 }
                        ],
                        layer: activeLayerName || '0',
                        _userEntityId: entityId2,
                        _userDrawn: true,
                        _tabId: activeTabId || 'mimari',
                    }));

                    // Undo-redo kaydı
                    const manager = window.undoRedoManager;
                    if (manager) {
                        const tx = manager.beginTransaction('Draw Line');
                        manager.addCommand(new DrawCommand(line));
                        manager.commitTransaction(tx);
                    }

                    if (tempLineRef.current) {
                        scene.remove(tempLineRef.current);
                        tempLineRef.current.geometry.dispose();
                        tempLineRef.current.material.dispose();
                    }

                    const tempGeometry = new THREE.BufferGeometry().setFromPoints([ep, ep]);
                    const tempMaterial = new THREE.LineBasicMaterial({ color: 0xffff00 });
                    const tempLine = new THREE.Line(tempGeometry, tempMaterial);
                    scene.add(tempLine);
                    tempLineRef.current = tempLine;

                    dispatch(setOperationData({ startPoint: { x: ep.x, y: ep.y, z: ep.z } }));
                    dispatch(setCommandMessage("sonraki noktayı seçin veya Enter ile bitirin."));
                    inputBuffer.current = '';
                }
            } else if (/\d|\./.test(e.key)) {
                inputBuffer.current += e.key;
            } else if (e.key === 'Backspace') {
                inputBuffer.current = inputBuffer.current.slice(0, -1);
            }
        };

        domElement.addEventListener('click', handleClick);
        domElement.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            domElement.removeEventListener('click', handleClick);
            domElement.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [commandType, step, data, scene, camera, renderer, dispatch, gridMode, objectSnap, snapPoints]);
};

export default useDrawLine;
